import { Close } from '@mui/icons-material';
import { Box, IconButton, Modal, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  useLazyGetApiDeliverablesByIdQuery,
  useLazyGetApiWorkflowsByIdQuery,
} from '../../../../../app/athenaApiGen';
import { setEditModalOpen } from '../../../../../app/deliverables/deliverablesSlice';
import { RootState } from '../../../../../app/store';
import { createEmptyDeliverable } from '../../../../deliverables/consts';
import DeliverableSummary from '../components/DeliverableSummary';
import StepperContent from '../components/StepperContent';
import { useDeliverableModalContext } from '../DeliverableModalContext';
import { DeliverableModalContainer } from '../StyledComponents';

const EditDeliverableModal = () => {
  const { isEditModalOpen: open, selectedRowId } = useSelector(
    (state: RootState) => state.deliverables,
  );
  const dispatch = useDispatch();
  const [error, setError] = useState<string | null>(null);

  const {
    setDeliverable,
    setSavedFiles,
    setUploadedFiles,
    setOriginalWorkflow,
  } = useDeliverableModalContext();
  const [fetchDeliverable] = useLazyGetApiDeliverablesByIdQuery();
  const [fetchWorkflow] = useLazyGetApiWorkflowsByIdQuery();

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      if (!open || !selectedRowId) return;
      try {
        const result = await fetchDeliverable({ id: selectedRowId }).unwrap();
        const workflow = await fetchWorkflow({
          id: result.workflowId ?? '',
        }).unwrap();
        if (!mounted) return;
        setDeliverable({
          deliverableTitle: result.deliverableTitle ?? '',
          locationId: result.locationId ?? '',
          deliverableTypeId: result.deliverableTypeId ?? '',
          dueDate: result.dueDate ?? undefined,
          description: result.description ?? '',
          workflowType: workflow?.workflowType ?? '',
          projectId: result.projectId ?? '',
        });
        setSavedFiles(result.attachments ?? []);
        setOriginalWorkflow({
          workflowId: result.workflowId,
          workflowState: workflow?.workflowState,
        });
        setError(null);
      } catch (err) {
        if (!mounted) return;
        setError('Failed to load deliverable');
      }
    };

    load();

    return () => {
      mounted = false;
    };
  }, [open, selectedRowId, fetchDeliverable]);

  const handleClose = () => {
    setError(null);
    setDeliverable(createEmptyDeliverable());
    setUploadedFiles([]);
    setSavedFiles([]);
    setOriginalWorkflow(null);
    dispatch(setEditModalOpen(false));
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <DeliverableModalContainer
        sx={{
          width: '80vw',
        }}
      >
        <Box
          sx={{
            marginBottom: 1,
            pb: 1,
            borderBottom: '2px solid var(--template-palette-divider)',
            justifyContent: 'space-between',
            display: 'flex',
            flexDirection: 'row',
          }}
        >
          <Typography variant="h5">Update Deliverable</Typography>
          <IconButton size="small" onClick={handleClose}>
            <Close />
          </IconButton>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            height: '100%',
            minHeight: 0,
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: 'background.paper',
              borderRight: '1px solid var(--template-palette-divider)',
              gap: 2,
              overflow: 'auto',
              width: '300px',
              height: '100%',
              p: 2.5,
            }}
          >
            <DeliverableSummary />
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              backgroundColor: 'background.default',
              overflow: 'auto',
              flex: 1,
            }}
          >
            <StepperContent />
          </Box>
        </Box>
      </DeliverableModalContainer>
    </Modal>
  );
};

export default EditDeliverableModal;
